package top.wcpe.allincore.dsl.compiler.pipeline

import top.wcpe.allincore.dsl.compiler.ast.*
import top.wcpe.allincore.dsl.compiler.diag.DiagnosticCollector
import top.wcpe.allincore.dsl.compiler.semantic.`native`.ClassRegistry
import top.wcpe.allincore.dsl.compiler.semantic.`native`.ResolveCtx

/**
 * Stage 4：内置宿主 object MemberCall lower pass。
 *
 * 在 SemanticChecker 之前运行，把 MemberCall(target=IdentRef("net"), method="request", ...)
 * 通过 ClassRegistry 描述符路由 lower 为专用 AST 节点（如 NetRequestCall）。
 *
 * 设计：完整递归 ScreenAst 所有表达式位置，对每个 MemberCall 尝试 resolve。
 */
class NativeClassResolverPass(
    private val registry: ClassRegistry,
    private val diag: DiagnosticCollector,
) {

    private lateinit var screenId: String

    /** 入口：展开 screen 中所有可 lower 的 MemberCall。 */
    fun expand(screen: ScreenAst): ScreenAst {
        screenId = screen.id
        // Stage 7a：把 user-defined object 注册到 registry，让 transformExpr 能识别
        registry.registerUserObjects(screen.classDecls)
        return screen.copy(
            root = transformElement(screen.root),
            funs = screen.funs.map { transformFunDecl(it) },
            // Stage 7c：user object fun body 内的 MemberCall（如 Object.method 互调）
            // 也需要递归 lower，否则 runtime 看到的仍是 MemberCall(IdentRef("Object"), ...)
            // 无法被 dispatchByIdentRef 路由到 IrFun
            classDecls = screen.classDecls.map { transformClassDecl(it) },
            onKeyLambda = screen.onKeyLambda?.let { transformLambda(it) },
            onMouseDownLambda = screen.onMouseDownLambda?.let { transformLambda(it) },
            onMouseUpLambda = screen.onMouseUpLambda?.let { transformLambda(it) },
            onMouseMoveLambda = screen.onMouseMoveLambda?.let { transformLambda(it) },
            openBody = screen.openBody?.let { transformStmtList(it) },
            closeBody = screen.closeBody?.let { transformStmtList(it) },
            showBody = screen.showBody?.let { transformStmtList(it) },
            hideBody = screen.hideBody?.let { transformStmtList(it) },
            papiBlocks = screen.papiBlocks.map { transformPapiBlock(it) },
            subscribeBlocks = screen.subscribeBlocks.map { transformSubscribeBlock(it) },
        )
    }

    // ===== 顶层结构 =====

    private fun transformFunDecl(decl: FunDecl): FunDecl =
        decl.copy(
            body = transformStmtList(decl.body),
            defaults = decl.defaults.map { it?.let { transformExpr(it) } },
        )

    /**
     * Stage 7c：递归 lower ClassDecl 内 fun body 中的 MemberCall。
     *
     * user object fun 写法上是"近闭包"——可以引用同 screen 内其它 user object，
     * 例如 `fun foo() { Helper.bar() }`。本方法保证这些 MemberCall 也被
     * NativeClassResolverPass 改写为 CallExpr(FunRefExpr("Helper#bar"), ...)，
     * 与顶层 fun 的处理路径完全一致。
     *
     * 同步对 [ClassDecl.nestedClasses] 递归，便于 Stage 8 嵌套 class 直接复用。
     */
    private fun transformClassDecl(decl: ClassDecl): ClassDecl =
        decl.copy(
            funs = decl.funs.map { transformFunDecl(it) },
            nestedClasses = decl.nestedClasses.map { transformClassDecl(it) },
        )

    private fun transformLambda(lambda: ExprAst.LambdaExpr): ExprAst.LambdaExpr =
        lambda.copy(body = transformStmtList(lambda.body))

    private fun transformElement(element: ElementAst): ElementAst {
        val newProps = element.props.map { pa ->
            pa.copy(value = transformExpr(pa.value))
        }
        val newModifiers = element.modifiers.map { modifier ->
            modifier.copy(fields = modifier.fields.map { it.copy(value = transformExpr(it.value)) })
        }
        val newChildren = element.children.map { transformElement(it) }
        val newSourceExpr = element.sourceExpr?.let { transformExpr(it) }
        return element.copy(
            props = newProps,
            modifiers = newModifiers,
            children = newChildren,
            sourceExpr = newSourceExpr,
        )
    }

    private fun transformPapiBlock(block: PapiBlockAst): PapiBlockAst =
        block.copy(placeholders = block.placeholders.map { ph ->
            ph.copy(
                bindExpr = ph.bindExpr?.let { transformExpr(it) },
                pipe = ph.pipe?.let { transformLambda(it) },
                pipeExpr = ph.pipeExpr?.let { transformExpr(it) },
                onChangeExpr = ph.onChangeExpr?.let { transformExpr(it) },
            )
        })

    private fun transformSubscribeBlock(block: SubscribeBlockAst): SubscribeBlockAst =
        block.copy(items = block.items.map { item ->
            item.copy(handler = transformExpr(item.handler))
        })

    // ===== 语句递归 =====

    private fun transformStmtList(stmts: List<StmtAst>): List<StmtAst> =
        stmts.map { transformStmt(it) }

    private fun transformStmt(s: StmtAst): StmtAst = when (s) {
        is StmtAst.ExprStmt -> s.copy(call = transformExpr(s.call))
        is StmtAst.AssignStmt -> s.copy(rhs = transformExpr(s.rhs))
        is StmtAst.VarDeclStmt -> s.copy(initializer = transformExpr(s.initializer))
        is StmtAst.IfStmt -> s.copy(
            cond = transformExpr(s.cond),
            thenBody = transformStmtList(s.thenBody),
            elseBody = transformStmtList(s.elseBody),
        )
        is StmtAst.WhileStmt -> s.copy(
            cond = transformExpr(s.cond),
            body = transformStmtList(s.body),
        )
        is StmtAst.ForInStmt -> s.copy(
            iterable = transformExpr(s.iterable),
            body = transformStmtList(s.body),
        )
        is StmtAst.WhenStmt -> s.copy(
            whenExpr = transformExpr(s.whenExpr) as ExprAst.WhenExpr,
        )
        is StmtAst.ReturnStmt -> s.copy(value = s.value?.let { transformExpr(it) })
        is StmtAst.TryStmt -> s.copy(
            tryBody = transformStmtList(s.tryBody),
            catchBody = transformStmtList(s.catchBody),
            finallyBody = transformStmtList(s.finallyBody),
        )
        // batch 语句：对 body 递归 lower 并返回 copy（与 TryStmt 的 tryBody 处理一致）
        is StmtAst.BatchStmt -> s.copy(body = transformStmtList(s.body))
        is StmtAst.FunDeclStmt -> s.copy(decl = transformFunDecl(s.decl))
        is StmtAst.BreakStmt, is StmtAst.ContinueStmt -> s
    }

    // ===== 表达式递归 =====

    private fun transformExpr(expr: ExprAst): ExprAst = when (expr) {
        is ExprAst.MemberCall -> {
            // 先递归子节点
            val transformed = expr.copy(
                target = transformExpr(expr.target),
                args = expr.args.map { transformExpr(it) },
                namedArgs = expr.namedArgs.map { it.copy(value = transformExpr(it.value)) },
            )
            // 尝试通过描述符 resolve
            val target = transformed.target
            when (target) {
                is ExprAst.IdentRef -> {
                    val host = registry.lookupHostObject(target.name)
                    val method = host?.lookupMethod(transformed.method)
                    if (method != null) {
                        val ctx = ResolveCtx(transformed.position, screenId)
                        method.resolve(transformed, diag, ctx) ?: transformed
                    } else transformed
                }
                is ExprAst.MemberAccess -> {
                    // Stage 8：识别 Outer.Inner.method(...) 嵌套调用
                    resolveNestedMemberCall(transformed, target) ?: transformed
                }
                else -> transformed
            }
        }
        is ExprAst.BinaryOp -> expr.copy(
            left = transformExpr(expr.left),
            right = transformExpr(expr.right),
        )
        is ExprAst.UnaryOp -> expr.copy(operand = transformExpr(expr.operand))
        is ExprAst.MemberAccess -> expr.copy(target = transformExpr(expr.target))
        is ExprAst.SafeMemberAccess -> expr.copy(target = transformExpr(expr.target))
        // M10 Stage 6：`target!!` 透传 target
        is ExprAst.NonNullAssert -> expr.copy(target = transformExpr(expr.target))
        is ExprAst.SafeMemberCall -> expr.copy(
            target = transformExpr(expr.target),
            args = expr.args.map { transformExpr(it) },
        )
        is ExprAst.Elvis -> expr.copy(
            left = transformExpr(expr.left),
            right = transformExpr(expr.right),
        )
        is ExprAst.Ternary -> expr.copy(
            cond = transformExpr(expr.cond),
            thenExpr = transformExpr(expr.thenExpr),
            elseExpr = transformExpr(expr.elseExpr),
        )
        is ExprAst.IfExpr -> expr.copy(
            cond = transformExpr(expr.cond),
            thenBody = transformStmtList(expr.thenBody),
            elseBody = transformStmtList(expr.elseBody),
        )
        is ExprAst.Interp -> expr.copy(parts = expr.parts.map { part ->
            when (part) {
                is ExprAst.Interp.InterpPart.ExprPart ->
                    ExprAst.Interp.InterpPart.ExprPart(transformExpr(part.expr))
                is ExprAst.Interp.InterpPart.TextPart -> part
            }
        })
        is ExprAst.ListLiteralExpr -> expr.copy(elements = expr.elements.map { transformExpr(it) })
        is ExprAst.MapLiteralExpr -> expr.copy(
            entries = expr.entries.map { it.copy(value = transformExpr(it.value)) },
        )
        is ExprAst.RangeExpr -> expr.copy(
            start = transformExpr(expr.start),
            endValue = transformExpr(expr.endValue),
        )
        is ExprAst.IndexExpr -> expr.copy(
            target = transformExpr(expr.target),
            index = transformExpr(expr.index),
        )
        is ExprAst.LambdaExpr -> expr.copy(body = transformStmtList(expr.body))
        is ExprAst.WhenExpr -> expr.copy(
            subject = transformExpr(expr.subject),
            branches = expr.branches.map { br ->
                br.copy(
                    values = br.values.map { transformExpr(it) },
                    body = transformStmtList(br.body),
                )
            },
            elseBody = expr.elseBody?.let { transformStmtList(it) },
        )
        is ExprAst.CallExpr -> expr.copy(
            callee = transformExpr(expr.callee),
            args = expr.args.map { transformExpr(it) },
            namedArgs = expr.namedArgs.map { it.copy(value = transformExpr(it.value)) },
        )
        is ExprAst.BuiltinCallExpr -> expr.copy(
            args = expr.args.map { transformExpr(it) },
            namedArgs = expr.namedArgs.map { it.copy(value = transformExpr(it.value)) },
        )
        is ExprAst.NetRequestCall -> expr.copy(
            payload = expr.payload.map { it.copy(value = transformExpr(it.value)) },
            callback = expr.callback?.let { transformExpr(it) },
            timeoutMs = expr.timeoutMs?.let { transformExpr(it) },
        )
        is ExprAst.NetSubscribeCall -> expr.copy(
            handler = transformExpr(expr.handler),
        )
        is ExprAst.AwaitExpr -> expr.copy(
            netRequestCall = expr.netRequestCall?.let { transformExpr(it) },
            suspendCall = expr.suspendCall?.let { transformExpr(it) },
            tailExpr = expr.tailExpr?.let { transformExpr(it) },
        )
        is ExprAst.IsExpr -> expr.copy(target = transformExpr(expr.target))
        // 叶子节点
        is ExprAst.Literal, is ExprAst.StateRef, is ExprAst.SessionRef, is ExprAst.PersistRef, is ExprAst.DerivedRef, is ExprAst.IdentRef,
        is ExprAst.LocalRef, is ExprAst.ThemeRef, is ExprAst.PropsRef,
        is ExprAst.NullLit, is ExprAst.FunRefExpr -> expr
    }

    // ===== Stage 8：嵌套 user object 调用 lower =====

    /**
     * Stage 8：尝试把 `Outer.Inner.method(args)` 形态的 MemberCall lower 为
     * `CallExpr(FunRefExpr("Outer#Inner#method"), args)`。
     *
     * 失败条件（任何一个不满足都返回 null，调用方保留原 MemberCall）：
     * - target MemberAccess 链无法收敛为 `IdentRef` 起点（含非 IdentRef/MemberAccess 节点）
     * - 拼出的点分路径不在 [registry] 的嵌套 object 注册表中
     * - 嵌套 ClassDecl 内不存在与 [call.method] 同名的 fun
     * - arity / 命名实参 name 与 fun 形参不匹配
     */
    private fun resolveNestedMemberCall(
        call: ExprAst.MemberCall,
        target: ExprAst.MemberAccess,
    ): ExprAst? {
        val nestedPath = buildNestedPath(target) ?: return null
        val nestedDecl = registry.lookupNestedObject(nestedPath) ?: return null
        val fn = nestedDecl.funs.firstOrNull { it.name == call.method } ?: return null

        // 复用 UserObjectMethodDescriptor.validateCallShape，确保嵌套与顶层 lower 行为一致
        if (!top.wcpe.allincore.dsl.compiler.semantic.`native`.methods
                .UserObjectMethodDescriptor.validateCallShape(fn, call)
        ) return null

        // 把点分路径转为 # 分隔的 FunRef key（与 ScreenEmitter funMap key 共享同一约定）
        val sep = top.wcpe.allincore.dsl.compiler.semantic.`native`.methods
            .UserObjectMethodDescriptor.FUN_REF_SEPARATOR
        val funRefName = nestedPath.replace(".", sep) + sep + call.method
        return ExprAst.CallExpr(
            callee = ExprAst.FunRefExpr(funRefName, call.position),
            args = call.args,
            position = call.position,
            namedArgs = call.namedArgs,
        )
    }

    /**
     * Stage 8：把嵌套 [ExprAst.MemberAccess] 链拼成点分路径。
     *
     * - `MemberAccess(IdentRef("A"), "B")` → `"A.B"`
     * - `MemberAccess(MemberAccess(IdentRef("A"), "B"), "C")` → `"A.B.C"`
     * - 中间出现非 IdentRef/MemberAccess 节点 → null（不是合法嵌套路径）
     */
    private fun buildNestedPath(target: ExprAst.MemberAccess): String? = when (val outer = target.target) {
        is ExprAst.IdentRef -> "${outer.name}.${target.member}"
        is ExprAst.MemberAccess -> buildNestedPath(outer)?.let { "$it.${target.member}" }
        else -> null
    }
}
